<template>
  <div>
    <v-container fluid class="pa-0 pb-6">
      <v-card outlined class="mx-auto px-2">
        <v-row>
          <v-col cols="12">
            <div class="px-2 pt-2 primary--text font-weight-bold">
              {{ $vuetify.lang.t("$vuetify.subscription_plans.free_plan") }}
            </div>
          </v-col>
        </v-row>

        <v-divider />

        <v-data-table
          :headers="freePlanHeaders"
          :items="freePlan"
          :loading="freePlanLoading"
          :loading-text="$vuetify.lang.t('$vuetify.subscription_plans.messages.loading')"
          hide-default-footer
          disable-pagination
        >
          <template v-slot:[`item.account_id`]="{ item }">
            <div class="d-flex align-center justify-center">
              <TruncateTooltip :text="item.account_id || 'N/A'" :limit="30" />

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="copyToClipboard(item.account_id)"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </template>

                <span>
                  {{ $vuetify.lang.t("$vuetify.common.copy_msg_txt") }}
                </span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:[`item.account_name`]="{ item }">
            <TruncateTooltip :text="item.account_name || 'N/A'" :limit="30" />
          </template>

          <template v-slot:[`item.project_id`]="{ item }">
            <div class="d-flex align-center justify-center">
              <TruncateTooltip :text="item.project_id || 'N/A'" :limit="30" />

              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    small
                    v-bind="attrs"
                    v-on="on"
                    @click="copyToClipboard(item.project_id)"
                  >
                    <v-icon>mdi-content-copy</v-icon>
                  </v-btn>
                </template>

                <span>
                  {{ $vuetify.lang.t("$vuetify.common.copy_msg_txt") }}
                </span>
              </v-tooltip>
            </div>
          </template>

          <template v-slot:[`item.project_name`]="{ item }">
            <TruncateTooltip :text="item.project_name || 'N/A'" :limit="30" />
          </template>

          <template v-slot:no-data>
            <div class="py-6 text-center">
              {{ $vuetify.lang.t("$vuetify.subscription_plans.messages.no_free_plan_found") }}
            </div>
          </template>
        </v-data-table>
      </v-card>
    </v-container>

    <v-container fluid class="pa-0">
      <v-card outlined class="mx-auto pa-4">
        <div class="primary--text font-weight-bold mb-4">
          {{ $vuetify.lang.t("$vuetify.subscription_plans.free_plan_settings") }}
        </div>

        <v-form ref="freePlanFormRef">
          <v-row>
            <v-col cols="12" md="6" class="pt-0">
              <v-text-field
                v-model.trim="freePlanForm.accountId"
                :label="$vuetify.lang.t('$vuetify.account_settings.account_id')"
                outlined
                :rules="[rules.required($vuetify.lang.t('$vuetify.account_settings.account_id'))]"
              />
            </v-col>

            <v-col cols="12" md="6" class="pt-0">
              <v-text-field
                v-model.trim="freePlanForm.projectId"
                :label="$vuetify.lang.t('$vuetify.subscription_plans.project_id')"
                outlined
                :rules="[rules.required($vuetify.lang.t('$vuetify.subscription_plans.project_id'))]"
              />
            </v-col>

            <v-col cols="12" class="pt-0">
              <v-checkbox
                v-model="freePlanForm.is_public"
                hide-details
                class="ma-0"
              >
                <template v-slot:label>
                  <div class="d-flex align-center">
                    <span>
                      {{ $vuetify.lang.t("$vuetify.subscription_plans.is_public") }}
                    </span>

                    <v-tooltip bottom max-width="350">
                      <template v-slot:activator="{ on }">
                        <span v-on="on" class="info-icon ml-2">i</span>
                      </template>

                      <span>
                        {{ $vuetify.lang.t("$vuetify.subscription_plans.hints.is_public") }}
                      </span>
                    </v-tooltip>
                  </div>
                </template>
              </v-checkbox>
            </v-col>

            <v-col cols="12" class="mt-2 d-flex justify-end">
              <v-btn
                color="primary"
                :loading="updatingFreePlan"
                @click="openConfirmationDialog"
              >
                {{ $vuetify.lang.t("$vuetify.common.update_btn") }}
              </v-btn>
            </v-col>
          </v-row>
        </v-form>

        <DialogModal
          v-model="confirmationDialog"
          :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
          :message="$vuetify.lang.t('$vuetify.subscription_plans.messages.free_plan_update_confirm_message')"
          :confirmButtonText="$vuetify.lang.t('$vuetify.common.confirm_btn')"
          :cancelButtonText="$vuetify.lang.t('$vuetify.common.cancel_btn')"
          :confirmBtnLoading="updatingFreePlan"
          @onConfirmation="confirmUpdate"
          @onCancel="closeConfirmationDialog"
        />
      </v-card>
    </v-container>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  CREATE_FREE_PLAN,
  LOAD_FREE_PLAN,
  ADD_ALERT,
} from "@/store/_actiontypes";
import validations from "@/helpers/validations";
import DialogModal from "@/components/common/DialogModal";
import TruncateTooltip from "@/components/common/tooltip/TruncateTooltip.vue";

export default {
  name: "FreePlanSettingsCard",

  components: {
    DialogModal,
    TruncateTooltip,
  },

  data() {
    return {
      freePlanForm: {
        accountId: "",
        projectId: "",
        is_public: false,
      },

      updatingFreePlan: false,
      confirmationDialog: false,
      freePlanPayload: null,

      freePlanHeaders: [
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.account_id"),
          align: "center",
          value: "account_id",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.account_name"),
          align: "center",
          value: "account_name",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.project_id"),
          align: "center",
          value: "project_id",
          sortable: false,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.subscription_plans.project_name"),
          align: "center",
          value: "project_name",
          sortable: false,
        },
      ],
    };
  },

  computed: {
    ...mapGetters("subscription_plans", [
      "freePlan",
      "freePlanLoading",
      "freePlanSuccessMessage",
      "freePlanErrorMessage",
    ]),

    rules() {
      return {
        required: (property) => validations.requiredTrimmed(property),
      };
    },
  },

  async created() {
    await this.fetchFreePlan();
  },

  methods: {
    ...mapActions("subscription_plans", {
      createFreePlan: CREATE_FREE_PLAN,
      loadFreePlan: LOAD_FREE_PLAN,
    }),

    ...mapActions("alert", {
      addAlert: ADD_ALERT,
    }),

    async fetchFreePlan() {
      try {
        await this.loadFreePlan();
      } catch (error) {
        this.addAlert({
          message: this.$vuetify.lang.t(
            "$vuetify.subscription_plans.messages.free_plan_load_failed"
          ),
          color: "error",
        });
      }
    },

    openConfirmationDialog() {
      const isValid = this.$refs.freePlanFormRef.validate();
      if (!isValid) return;

      this.freePlanPayload = {
        account_id: this.freePlanForm.accountId,
        project_id: this.freePlanForm.projectId,
        is_public: this.freePlanForm.is_public,
      };

      this.confirmationDialog = true;
    },

    closeConfirmationDialog() {
      this.confirmationDialog = false;
      this.freePlanPayload = null;
    },

    async confirmUpdate() {
      if (!this.freePlanPayload) return;

      this.updatingFreePlan = true;

      try {
        await this.createFreePlan(this.freePlanPayload);
        await this.loadFreePlan();

        this.addAlert({
          message:
            this.freePlanSuccessMessage ||
            this.$vuetify.lang.t(
              "$vuetify.subscription_plans.messages.free_plan_flag_update_success"
            ),
          color: "success",
        });

        this.freePlanForm = {
          accountId: "",
          projectId: "",
          is_public: false,
        };

        this.$nextTick(() => {
          this.$refs.freePlanFormRef?.resetValidation();
        });
      } catch (error) {
        this.addAlert({
          message:
            this.freePlanErrorMessage ||
            this.$vuetify.lang.t(
              "$vuetify.subscription_plans.messages.free_plan_flag_update_failed"
            ),
          color: "error",
        });
      } finally {
        this.updatingFreePlan = false;
        this.closeConfirmationDialog();
      }
    },
    copyToClipboard(text) {
      navigator.clipboard.writeText(text).then(
        () => {
          this.addAlert({
            message: this.$vuetify.lang.t(
              "$vuetify.common.copied_msg_txt"
            ),
            color: "success",
          });
        },
        err => {
          console.error("Could not copy text: ", err);
        }
      );
    },
  },
};
</script>